import type { CSSProperties } from "react";

export type OrganizerVisualIdentityMode = "tnp_standard" | "custom";
export type OrganizerVisualIdentityBackgroundMode = "blue" | "black" | "white";

export type OrganizerVisualIdentityColors = {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
};

export type OrganizerVisualIdentity = {
  mode: OrganizerVisualIdentityMode;
  backgroundMode?: OrganizerVisualIdentityBackgroundMode;
  colors: OrganizerVisualIdentityColors;
  defaultColors?: OrganizerVisualIdentityColors;
  isCustom?: boolean;
  logoUrl?: string | null;
  updatedAt?: string | null;
};

export const DEFAULT_TNP_VISUAL_IDENTITY_COLORS: OrganizerVisualIdentityColors = {
  primaryColor: "#2B9CDF",
  secondaryColor: "#E53682",
  accentColor: "#7BC4ED",
};

export const DEFAULT_TNP_VISUAL_IDENTITY: OrganizerVisualIdentity = {
  mode: "tnp_standard",
  backgroundMode: "blue",
  colors: DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
  defaultColors: DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
  isCustom: false,
};

export function isValidHexColor(value: string) {
  return /^#[0-9a-fA-F]{6}$/.test(String(value || "").trim());
}

export function normalizeHexColor(value: string) {
  const raw = String(value || "").trim();
  return isValidHexColor(raw) ? raw.toUpperCase() : raw;
}

export function getEffectiveIdentityColors(
  identity?: OrganizerVisualIdentity | null,
) {
  if (!identity || identity.mode !== "custom") {
    return DEFAULT_TNP_VISUAL_IDENTITY_COLORS;
  }
  const colors = identity.colors || DEFAULT_TNP_VISUAL_IDENTITY_COLORS;
  if (
    !isValidHexColor(colors.primaryColor) ||
    !isValidHexColor(colors.secondaryColor) ||
    !isValidHexColor(colors.accentColor)
  ) {
    return DEFAULT_TNP_VISUAL_IDENTITY_COLORS;
  }
  return {
    primaryColor: normalizeHexColor(colors.primaryColor),
    secondaryColor: normalizeHexColor(colors.secondaryColor),
    accentColor: normalizeHexColor(colors.accentColor),
  };
}

function colorChannelToLinear(channel: number) {
  const value = channel / 255;
  return value <= 0.03928
    ? value / 12.92
    : Math.pow((value + 0.055) / 1.055, 2.4);
}

function luminance(hex: string) {
  const normalized = normalizeHexColor(hex).replace("#", "");
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return (
    0.2126 * colorChannelToLinear(r) +
    0.7152 * colorChannelToLinear(g) +
    0.0722 * colorChannelToLinear(b)
  );
}

export function contrastRatio(foreground: string, background: string) {
  if (!isValidHexColor(foreground) || !isValidHexColor(background)) return 1;
  const a = luminance(foreground);
  const b = luminance(background);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

export function hasLowOrganizerIdentityContrast(
  colors: OrganizerVisualIdentityColors,
  backgroundMode: OrganizerVisualIdentityBackgroundMode = "blue",
) {
  const readableBackgrounds =
    backgroundMode === "white"
      ? ["#FFFFFF", "#F4F7FA"]
      : ["#020817", "#071833"];
  return [colors.primaryColor, colors.secondaryColor, colors.accentColor].some(
    (color) =>
      !isValidHexColor(color) ||
      readableBackgrounds.some(
        (background) => contrastRatio(color, background) < 3,
      ),
  );
}

export function organizerIdentityStyle(
  identity?: OrganizerVisualIdentity | null,
) {
  const colors = getEffectiveIdentityColors(identity);
  const backgroundMode =
    identity?.backgroundMode === "black" || identity?.backgroundMode === "white"
      ? identity.backgroundMode
      : "blue";
  return {
    "--organizer-primary": colors.primaryColor,
    "--organizer-secondary": colors.secondaryColor,
    "--organizer-accent": colors.accentColor,
    "--organizer-background": `var(--color-organizer-background-${backgroundMode})`,
    "--organizer-surface": `var(--color-organizer-surface-${backgroundMode})`,
    "--organizer-surface-strong": `var(--color-organizer-surface-${backgroundMode}-strong)`,
    "--organizer-text": `var(--color-organizer-text-${backgroundMode})`,
    "--organizer-text-soft": `var(--color-organizer-text-${backgroundMode}-soft)`,
    "--organizer-text-muted": `var(--color-organizer-text-${backgroundMode}-muted)`,
    "--organizer-border": `var(--color-organizer-border-${backgroundMode})`,
  } as CSSProperties;
}
